/**
 * PDF/Word dropzone used on ATS upload and the empty dashboard.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useRequireAuth } from '../contexts/AuthGateContext';
import { requireCookieChoice } from '../utils/cookieConsent';
import { trackCvUpload } from '../utils/analytics';
import '../pages/FlowPages.css';

const CVFileDropzone = ({ source }) => {
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();
  const { t } = useLanguage();
  const requireAuth = useRequireAuth();

  const onDrop = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    const validTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
    ];
    const validExtensions = ['.pdf', '.docx', '.doc'];
    const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`;

    if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
      toast.error(t('flow.errFileType'));
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('flow.errFileSize'));
      return;
    }

    if (!(await requireAuth())) {
      return;
    }

    await requireCookieChoice();

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', file.name.replace(/\.[^/.]+$/, ''));

      const response = await axios.post('/api/cvs/upload/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      trackCvUpload({ source });
      toast.success(t('flow.uploadOk'));
      navigate(`/dashboard/cv/${response.data.id}`);
    } catch (error) {
      console.error('Error uploading CV:', error);
      toast.error(error.response?.data?.error || t('flow.uploadFail'));
    } finally {
      setUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc'],
    },
    multiple: false,
    disabled: uploading,
  });

  const inputProps = getInputProps();

  return (
    <div
      {...getRootProps()}
      className={`flow-dropzone${isDragActive ? ' is-active' : ''}${uploading ? ' is-disabled' : ''}`}
      aria-disabled={uploading}
    >
      <input {...inputProps} aria-label={t('flow.chooseFile')} />
      {isDragActive ? (
        <p className="flow-dropzone__text">{t('flow.dropHere')}</p>
      ) : (
        <>
          <p className="flow-dropzone__text">{t('flow.dropOrClick')}</p>
          <p className="flow-dropzone__hint">{t('flow.formats')}</p>
        </>
      )}
      <p className="flow-dropzone__hint" role="status" aria-live="polite">
        {uploading ? t('flow.processing') : '\u00a0'}
      </p>
    </div>
  );
};

CVFileDropzone.propTypes = {
  source: PropTypes.string,
};

CVFileDropzone.defaultProps = {
  source: 'ats',
};

export default CVFileDropzone;
